import { useLiveQuery } from 'dexie-react-hooks';
import type { AppNotification } from '@app/shared';
import { api, OfflineError } from './api.ts';
import { fetchCapabilities } from './capabilities.ts';
import { db } from './db.ts';

/** In-app inbox, cached on the device so it can be read offline. */

export async function refreshInbox(): Promise<void> {
  try {
    const list = await api<AppNotification[]>('/api/notifications');
    await db.transaction('rw', db.notifications, async () => {
      // A read made on this device a moment ago may not have reached the server yet; keep it read.
      const readHere = new Map((await db.notifications.toArray()).filter((n) => n.readAt).map((n) => [n.id, n.readAt]));
      await db.notifications.clear();
      await db.notifications.bulkPut(list.map((n) => ({ ...n, readAt: n.readAt ?? readHere.get(n.id) ?? null })));
    });
  } catch (err) {
    if (!(err instanceof OfflineError)) throw err;
  }
}

export async function markAllRead(): Promise<void> {
  const now = new Date().toISOString();
  await db.notifications.toCollection().modify((n) => {
    n.readAt ??= now;
  });
  await api('/api/notifications/read', { method: 'POST', body: { all: true } }).catch(() => undefined);
}

export async function markRead(id: string): Promise<void> {
  await db.notifications.update(id, { readAt: new Date().toISOString() });
  await api('/api/notifications/read', { method: 'POST', body: { ids: [id] } }).catch(() => undefined);
}

export function useInbox(): AppNotification[] | undefined {
  return useLiveQuery(() => db.notifications.orderBy('createdAt').reverse().toArray(), []);
}

export function useUnreadCount(): number {
  return useLiveQuery(() => db.notifications.filter((n) => !n.readAt).count(), []) ?? 0;
}

// --- Device push (Web Push) ---------------------------------------------------

function urlBase64ToBytes(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * insecure: the page isn't on HTTPS (or localhost) — browsers only allow push there.
 * no-worker: HTTPS, but the offline service worker isn't running (e.g. development build).
 */
export type PushState = 'insecure' | 'unsupported' | 'no-worker' | 'not-configured' | 'denied' | 'off' | 'on';

const pushSupported = () => 'serviceWorker' in navigator && 'PushManager' in window && typeof Notification !== 'undefined';

export async function getPushState(): Promise<PushState> {
  if (typeof window === 'undefined') return 'unsupported';
  if (!window.isSecureContext) return 'insecure';
  if (!pushSupported()) return 'unsupported';
  const caps = await fetchCapabilities().catch(() => null);
  if (!caps?.webPush.configured) return 'not-configured';
  if (Notification.permission === 'denied') return 'denied';
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return 'no-worker';
  const sub = await reg.pushManager.getSubscription();
  return sub ? 'on' : 'off';
}

/** Ask for permission (call from a click, never on page load), subscribe, and register the device with the server. */
export async function enablePush(): Promise<PushState> {
  const caps = await fetchCapabilities();
  if (!caps.webPush.configured || !caps.webPush.publicKey) return 'not-configured';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return 'no-worker';
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToBytes(caps.webPush.publicKey) }));
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await api('/api/notifications/push/subscriptions', {
    method: 'POST',
    body: { endpoint: json.endpoint, keys: json.keys, userAgent: navigator.userAgent.slice(0, 300) },
  });
  return 'on';
}

export async function disablePush(): Promise<void> {
  if (typeof window === 'undefined' || !pushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  await api('/api/notifications/push/subscriptions', { method: 'DELETE', body: { endpoint: sub.endpoint } }).catch(() => undefined);
  await sub.unsubscribe();
}

export interface PushTestResult {
  configured: boolean;
  devices: number;
  delivered: number;
  removed: number;
  failed: number;
}

/** Plain-language outcome of a test push, from what the push service actually answered. */
export function describePushTest(r: PushTestResult): { message: string; ok: boolean } {
  if (!r.configured) return { ok: false, message: "Saved to your inbox. Push isn't set up on this server, so nothing was sent to devices." };
  if (r.devices === 0) return { ok: false, message: 'Saved to your inbox. No device has push turned on for your account yet.' };
  if (r.delivered > 0)
    return {
      ok: true,
      message: `Sent to ${r.delivered} device${r.delivered === 1 ? '' : 's'}. It should appear in a few seconds.${r.removed ? ` Removed ${r.removed} that no longer accept notifications.` : ''}`,
    };
  return {
    ok: false,
    message: r.removed
      ? 'Your devices no longer accept notifications; turn push on again on each device.'
      : 'The push service did not accept the message. Try again later.',
  };
}
